'use client';

import { memo, useState } from 'react';

import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import type {
  CharacterConfig,
  EyeColor,
  Gender,
  HairColor,
  HairStyle,
  StyleType,
} from './types';

interface Step3PhysicalDetailsProps {
  config: CharacterConfig;
  updateConfig: (updates: Partial<CharacterConfig>) => void;
  onBack: () => void;
  onNext: () => void;
}

// Eye/hair swatches use the photographic AVIF images (converted from the
// eyes/ and hair/ folders — see public/images/character-creator/3rd-step).
// Order matches the design.
const EYE_IMG = '/images/character-creator/3rd-step/eyes';
const HAIR_IMG = '/images/character-creator/3rd-step/hair';

const eyeColors: EyeColor[] = [
  'black',
  'brown',
  'hazel',
  'green',
  'blue',
  'purple',
  'pink',
  'gray',
];

const hairColors: HairColor[] = [
  'black',
  'brown',
  'auburn',
  'blonde',
  'red',
  'green',
  'blue',
  'purple',
  'silver',
  'white',
];

const hairStyles: HairStyle[] = [
  'long',
  'short',
  'medium',
  'wavy',
  'curly',
  'straight',
  'bob',
];

function getHairStyleImage(id: string, style: StyleType, gender: Gender) {
  // realistic/female not in card-images, fall back to existing folder
  if (style === 'realistic' && gender === 'female') {
    return `/images/character-creator/3rd-step/realistic/female/${id}.avif`;
  }
  return `/images/character-creator/card-images/3rd-step/${style}/${gender}/${id}/1.avif`;
}

function ImageCard({
  src,
  alt,
  isSelected,
  onClick,
}: {
  src: string;
  alt: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  const [imgError, setImgError] = useState(false);

  return (
    <button
      onClick={onClick}
      className={`relative aspect-[4/5] overflow-hidden rounded-md transition-all duration-300 sm:rounded-lg ${
        isSelected
          ? 'border-2 border-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.5)]'
          : 'border border-white/10 hover:border-white/30'
      }`}
    >
      {!imgError ? (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <span className="px-1 text-center text-[9px] font-medium text-gray-400 sm:text-[10px]">
            {alt}
          </span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
      <div className="absolute right-0 bottom-0 left-0 p-1.5 sm:p-1">
        <h3 className="text-center text-xs font-bold text-white sm:text-[8px] md:text-[10px]">
          {alt}
        </h3>
      </div>
      {isSelected && (
        <div className="absolute top-0.5 right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-pink-500 sm:top-1 sm:right-1 sm:h-3.5 sm:w-3.5">
          <Check className="h-2 w-2 text-white sm:h-2.5 sm:w-2.5" />
        </div>
      )}
    </button>
  );
}

export const Step3PhysicalDetails = memo(function Step3PhysicalDetails({
  config,
  updateConfig,
  onBack,
  onNext,
}: Step3PhysicalDetailsProps) {
  const { t } = useTranslation('common');

  return (
    <div className="mx-auto max-w-5xl px-2 sm:px-4 lg:px-0">
      {/* Title */}
      <div className="mb-2 text-center sm:mb-3">
        <h1 className="mb-0.5 text-lg font-bold text-white sm:mb-1 sm:text-xl md:text-2xl">
          <Trans i18nKey="common:characterCreator.titleDesign" />
        </h1>
        <p className="bg-gradient-to-r from-red-400 via-pink-400 to-pink-600 bg-clip-text text-[10px] font-semibold text-transparent sm:text-xs md:text-sm">
          <Trans i18nKey="common:characterCreator.step3PhysicalDetails" />
        </p>
      </div>

      {/* Hair Style */}
      <div className="mb-2 sm:mb-3">
        <h3 className="mb-1.5 text-center text-[10px] font-semibold text-white sm:mb-2 sm:text-xs md:text-sm">
          <Trans i18nKey="common:characterCreator.secHairStyle" />
        </h3>
        {/* 4 cards on the first row, 3 centered beneath (same card width:
            the second row spans 3/4 of the container). */}
        <div className="mx-auto max-w-full sm:max-w-sm md:max-w-xl lg:max-w-2xl">
          <div className="grid grid-cols-4 gap-2">
            {hairStyles.slice(0, 4).map((id) => (
              <ImageCard
                key={id}
                src={getHairStyleImage(id, config.style, config.gender)}
                alt={t(`characterCreator.options.hairStyle.${id}`)}
                isSelected={config.hairStyle === id}
                onClick={() => updateConfig({ hairStyle: id })}
              />
            ))}
          </div>
          <div className="mx-auto mt-2 grid w-3/4 grid-cols-3 gap-2">
            {hairStyles.slice(4).map((id) => (
              <ImageCard
                key={id}
                src={getHairStyleImage(id, config.style, config.gender)}
                alt={t(`characterCreator.options.hairStyle.${id}`)}
                isSelected={config.hairStyle === id}
                onClick={() => updateConfig({ hairStyle: id })}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Eye + Hair colour — boxed panel (matches step 2) */}
      <div
        className="mx-auto mb-2 max-w-full rounded-2xl p-3 sm:mb-3 sm:max-w-2xl sm:p-4"
        style={{
          border: '1px solid rgba(236,72,153,0.45)',
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        {/* Eye Color */}
        <div className="mb-2 sm:mb-3">
          <h3 className="mb-1.5 text-center text-[10px] font-semibold text-white sm:mb-2 sm:text-xs md:text-sm">
            <Trans i18nKey="common:characterCreator.secEyeColor" />
          </h3>
          <div
            className="mx-auto max-w-full sm:max-w-2xl"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
              justifyItems: 'center',
              columnGap: 4,
            }}
          >
            {eyeColors.map((id) => (
              <button
                key={id}
                onClick={() => updateConfig({ eyeColor: id })}
                className="flex flex-col items-center gap-1"
              >
                <span
                  className={`relative h-9 w-9 overflow-hidden rounded-lg transition-all sm:h-11 sm:w-11 ${
                    config.eyeColor === id
                      ? 'scale-105 ring-2 ring-pink-500'
                      : 'hover:scale-105'
                  }`}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={`${EYE_IMG}/${id}.avif`}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </span>
                <span className="text-[8px] font-medium text-white sm:text-xs">
                  {t(`characterCreator.options.eyeColor.${id}`)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Hair Color */}
        <div className="mb-2 sm:mb-3">
          <h3 className="mb-1.5 text-center text-[10px] font-semibold text-white sm:mb-2 sm:text-xs md:text-sm">
            <Trans i18nKey="common:characterCreator.secHairColor" />
          </h3>
          <div
            className="mx-auto max-w-full sm:max-w-2xl"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
              justifyItems: 'center',
              columnGap: 2,
            }}
          >
            {hairColors.map((id) => (
              <button
                key={id}
                onClick={() => updateConfig({ hairColor: id })}
                className="flex flex-col items-center gap-1"
              >
                <span
                  className={`relative h-7 w-7 overflow-hidden rounded-lg transition-all sm:h-11 sm:w-11 ${
                    config.hairColor === id
                      ? 'scale-105 ring-2 ring-pink-500'
                      : 'hover:scale-105'
                  }`}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={`${HAIR_IMG}/${id}.avif`}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </span>
                <span className="text-[8px] font-medium text-white sm:text-xs">
                  {t(`characterCreator.options.hairColor.${id}`)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-2 flex justify-center gap-2 sm:mt-3 sm:gap-2.5">
        <button
          onClick={onBack}
          className="rounded-lg border border-white/30 bg-transparent px-4 py-1.5 text-[10px] font-semibold text-white transition-all hover:border-white/50 hover:bg-white/5 sm:border-2 sm:px-6 sm:py-2 sm:text-xs md:px-8"
        >
          <Trans i18nKey="common:characterCreator.backButton" />
        </button>

        <button
          onClick={onNext}
          className="rounded-lg bg-gradient-to-r from-pink-600 via-pink-500 to-pink-700 px-6 py-1.5 text-[10px] font-bold text-white shadow-lg shadow-pink-500/30 transition-all hover:from-pink-700 hover:via-pink-600 hover:to-pink-800 sm:px-8 sm:py-2 sm:text-xs md:px-10"
        >
          <Trans i18nKey="common:characterCreator.nextButton" />
        </button>
      </div>
    </div>
  );
});
